import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Box, IconButton, Tooltip } from "@mui/material";
import { GridSortItem } from "@mui/x-data-grid";
import { useQueryClient } from "@tanstack/react-query";
import { capitalize } from "lodash";
import { useMemo, useState } from "react";
import {
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  CLAIM_NOT_COMPLETELY_POSTED_MSG,
  CLAIM_POSTING_STATUS_PARTIALLY_PAID,
} from "@/pages/app/provider/billings/billing-constant";
import CustomDataGridV2 from "../../../../../../../components/CustomDataGridV2";
import Flex from "../../../../../../../components/Flex";
import { GlobalTableHeightWrapper } from "../../../../../../../components/GlobalTableHeightWrapper";
import RenderWithTooltip from "../../../../../../../components/RenderWithTooltip";
import StatusChip from "../../../../../../../components/StatusChip";
import Text from "../../../../../../../components/Text";
import ActionMenu from "../../../../../../../components/app/ActionMenu";
import DeleteModal, {
  useDeleteDisClosure,
} from "../../../../../../../components/app/DeleteModal";
import { useRoleController } from "../../../../../../../components/useRoleController";
import { PROVIDER_PATHS } from "../../../../../../../constants/paths";
import useDisclosure from "../../../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../../../hooks/useSnackbar";
import { useAddElectronicClaim } from "../../../../../../../queries/billings/claims/use-add-electronic-claim";
// Superseded by the claim-by-id fetch inside ClaimEraDetailsTable.
// import { useGetClaimLite } from "../../../../../../../queries/billings/claims/use-get-claim-lite";
import { useGetClaimsV2 } from "../../../../../../../queries/billings/claims/use-get-claims-v2";
import { useArchiveClaims } from "../../../../../../../queries/billings/claims/use-patient-claims-archive";
import { useProvidersList } from "../../../../../../../queries/provider/setting/use-providers-list";
import {
  ClaimControllerApi,
  ClaimRequest,
  ClaimRequestVendorTypeEnum,
} from "../../../../../../../services/api";
import { NonNullableDate } from "../../../../../../../utils/date-time-utility";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  applyDefaultColumnSettings,
  filterActionCol,
  formatClaimType,
  getCommaSeparatedValues,
  getConfigObject,
  getErrorSnackbar,
  getFormattedId,
  getSuccessSnackbar,
  getValuesFromArrayOfObjects,
  getXtenantId,
  replaceUnderScoreAddSpace,
  roleFilter,
} from "../../../../../../../utils/utils";
import PrintSuperBill from "../../../../billings/PrintSuperBill";
import { PLACES_OF_SERVICE_MAPPER } from "../../../../billings/add-super-bill-components/places-of-service";
import ChangeClaimStatus from "../../../../billings/claims/ChangeClaimStatus";
import ClaimEraDetailsTable from "../../../../billings/claims/ClaimEraDetailsTable";
import ClearingHouseResponseModal from "../../../../billings/claims/ClearingHouseResponseModal";
import ViewPaperClaimDrawer from "../../../../billings/claims/ViewPaperClaimDrawer";
// Superseded by the ERA procedure breakdown rendered in the accordion detail
// row (see ClaimEraDetailsTable). Kept for reference.
// import {
//   buildCptChildRows,
//   mapLiteToCptRows,
// } from "../../../../billings/claims/claim-cpt-breakdown";
import NavSelectClaimTypeModal from "../../../../billings/claims/updateClaims/NavSelectClaimTypeModal";
import SendBulkEmailModal from "../../../../communications/email/SendBulkEmailModal";
import UploadFaxDocumentDrawer from "../../../../communications/fax/UploadFaxDocumentDrawer";
import { DOCUMENT_INITIAL_VALUE } from "../../../../communications/fax/fax-document-constant";
import { FinancialOutletContext } from "./financial-filters-context";

const PatientClaims = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Origin so the claim screen's Back can return to this patient-chart page
  // (instead of the Billing area). Only set when launched from the chart.
  const fromPatientChart = location.pathname + location.search;
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);
  const [selectedSuperBillUuid, setSelectedSuperBillUuid] = useState<
    string | null
  >(null);

  // Per-claim accordion (mirrors the Billing Claim tab). Only one claim is
  // expanded at a time; the open claim's ERA procedure breakdown is fetched by
  // ClaimEraDetailsTable from the claim uuid alone.
  const [expandedClaimId, setExpandedClaimId] = useState<string | null>(null);
  const toggleClaimExpand = (claimUuid: string) => {
    if (!claimUuid) return;
    setExpandedClaimId((prev) => (prev === claimUuid ? null : claimUuid));
  };

  const { mutateAsync: archieveAPI } = useArchiveClaims();
  const [, setSearchParams] = useSearchParams();
  const { hasReadOnly } = useRoleController("Financials");
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const { mutateAsync: addElectronicClaim } = useAddElectronicClaim();
  const patientId = useParams()?.patientId ?? "";

  const { claimsFilter: filters, claimsSearchKey: providerSearchKey } =
    useOutletContext<FinancialOutletContext>();

  const { data: providersList } = useProvidersList({
    pagination: DEF_PAGINATION,
    searchKey: providerSearchKey,
    xTENANTID: getXtenantId(),
    enabled: !!providerSearchKey,
  });

  const providerUuids = useMemo(() => {
    if (!providerSearchKey) return undefined;
    const uuids = (providersList?.data?.content ?? [])
      .map((p) => p?.uuid)
      .filter(Boolean);
    // Non-empty search with zero matches: ensure the API returns no claims
    // rather than silently returning all.
    return uuids.length > 0 ? uuids : ["__NO_PROVIDER_MATCH__"];
  }, [providerSearchKey, providersList?.data?.content]);

  const claimStatusesParam = filters?.claim_statuses?.length
    ? getCommaSeparatedValues(filters?.claim_statuses, "value")
    : undefined;

  const selectedClaimTypes = useMemo(
    () => getValuesFromArrayOfObjects(filters?.claim_types ?? [], "value"),
    [filters?.claim_types]
  );

  const {
    data: claimsList,
    isLoading: claimsDataLoading,
    isFetching: isClaimFetching,
    refetch,
  } = useGetClaimsV2({
    dateRange: "LAST_90_DAYS",
    patientUuids: [patientId],
    providerUuids,
    claimStatuses: claimStatusesParam,
    gridSorting,
    pagination,
  });

  const filteredRows = useMemo(() => {
    const rows = claimsList?.data?.content ?? [];
    if (!selectedClaimTypes?.length) return rows;
    return rows.filter((row) => selectedClaimTypes.includes(row?.claimType));
  }, [claimsList?.data?.content, selectedClaimTypes]);

  const {
    opened: openedDeleteModal,
    open: openDeleteModal,
    close: closeDeleteModal,
    deleteId,
  } = useDeleteDisClosure(false);

  const navSelectClaimTypeDisc = useDisclosure(false);
  const waystarResponseDisc = useDisclosure();
  const viewPaperClaimDisc = useDisclosure();
  const createFaxClaimDisc = useDisclosure();
  const changeClaimStatusDisclosureHost = useDisclosure();
  const sendMailDisclosure = useDisclosure();

  const { hasView: hasViewClaims, hasUpdate: hasUpdateClaims } =
    useRoleController("Generate Claims");
  const { hasUpdate: hasUpdateClaimStatus } = useRoleController(
    "Change claim status"
  );
  const { hasUpdate: hasUpdateWaystarResponse } = useRoleController(
    "Claim status request responce"
  );

  function handleElectronicClaim({
    claimType,
    claimUuid,
  }: {
    claimType: ClaimRequestVendorTypeEnum;
    claimUuid: string;
  }) {
    if (!claimUuid) {
      showSnackbar({
        message: "Claim ID is required to submit electronic claim",
        type: "error",
      });
      return;
    }
    const electronicClaimParams: ClaimRequest = {
      claimUuids: [claimUuid ?? ""],
      vendorType: claimType,
    };
    showElectronicClaimToastAndNavigate(
      addElectronicClaim({ claim: electronicClaimParams })
    );
  }

  const showElectronicClaimToastAndNavigate = (apiCall: Promise<any>) => {
    return apiCall
      ?.then((res) => {
        navSelectClaimTypeDisc.close();
        showSnackbar(getSuccessSnackbar(res));
        queryClient.invalidateQueries({ queryKey: ["get-claims-list-v2"] });
        // navigate(PROVIDER_PATHS.BILLING);
        navSelectClaimTypeDisc?.close();
      })
      .catch((err) => showSnackbar(getErrorSnackbar(err)));
  };

  const COLUMNS = applyDefaultColumnSettings<any>(
    [
      {
        // Expand control. The detail row carries the claim's ERA procedure
        // breakdown and spans every column (colSpan below).
        field: "__expand",
        headerName: "",
        width: 44,
        sortable: false,
        disableColumnMenu: true,
        hideSortIcons: true,
        renderCell: ({ row }) => {
          if (row?.__detail) {
            return (
              <Box sx={{ width: "100%" }}>
                <ClaimEraDetailsTable claimUuid={row?.parentUuid} />
              </Box>
            );
          }
          if (row?.__cptChild) return null;
          const isExpanded = expandedClaimId === row?.uuid;
          return (
            <IconButton
              size="small"
              onClick={() => toggleClaimExpand(row?.uuid)}
            >
              {isExpanded ? (
                <KeyboardArrowUpIcon fontSize="small" />
              ) : (
                <KeyboardArrowDownIcon fontSize="small" />
              )}
            </IconButton>
          );
        },
      },
      {
        field: "uuid",
        flex: 0.3,
        minWidth: 50,
        disableColumnMenu: true,
        headerName: "Claim ID",
        renderCell: ({ row }) => {
          if (row?.__detail) return null;
          // ECRH-5389 — child rows show the CPT code in place of the Claim ID.
          if (row?.__cptChild) {
            return (
              <Text isTabletext sx={{ pl: 1 }}>{`CPT ${row?.cptCode}`}</Text>
            );
          }
          return (
            <Text isTabletext>{row?.uuid?.split("-")?.[0]?.toUpperCase()}</Text>
          );
        },
      },
      {
        field: "claimType",
        flex: 0.4,
        minWidth: 70,
        disableColumnMenu: true,
        headerName: "Claim Type",
        renderCell: ({ value }) => (value ? formatClaimType(value) : "-"),
      },
      {
        field: "dateOfService",
        flex: 0.4,
        minWidth: 80,
        disableColumnMenu: true,
        headerName: "Visit ID",
        renderCell: ({ row }) => (
          <Box>
            <Text isTabletext>
              {row?.encounterId
                ? row?.encounterId?.split("-")?.[0]?.toUpperCase()
                : "-"}
            </Text>
            <Text isTabletext sx={{ color: "#667085", lineHeight: 1.2 }}>
              {NonNullableDate(row?.dateOfService)}
            </Text>
          </Box>
        ),
      },
      {
        field: "posCode",
        flex: 0.25,
        minWidth: 50,
        disableColumnMenu: true,
        headerName: "POS",
        sortable: false,
        renderCell: ({ row }) => {
          const desc = row?.posCode
            ? PLACES_OF_SERVICE_MAPPER?.[row.posCode]
            : "-";

          return <Tooltip title={desc}>{row?.posCode}</Tooltip>;
        },
      },
      {
        field: "providerName",
        flex: 0.4,
        minWidth: 80,
        disableColumnMenu: true,
        headerName: "Provider",
        renderCell: ({ row }) => {
          return (
            <Flex gap={"10px"}>
              <Box>
                <Text isTabletext marginBottom={"1px"}>
                  {row?.providerName ?? ""}
                </Text>
              </Box>
            </Flex>
          );
        },
      },
      {
        field: "payerName",
        flex: 0.5,
        minWidth: 90,
        disableColumnMenu: true,
        headerName: "Payer",
        renderCell: ({ row }) => {
          return (
            <RenderWithTooltip value={row?.insurancePayer?.payerName ?? ""} />
          );
        },
      },
      {
        field: "insuranceType",
        flex: 0.3,
        minWidth: 60,
        disableColumnMenu: true,
        headerName: "Billed As",
      },
      {
        field: "total",
        flex: 0.3,
        minWidth: 60,
        disableColumnMenu: true,
        headerName: "Claim Amount",
        renderCell: ({ row }) => (row?.total ? `$ ${row?.total}` : "-"),
      },
      {
        field: "paidAmount",
        flex: 0.2,
        minWidth: 40,
        disableColumnMenu: true,
        headerName: "Paid",
        renderCell: ({ value }) => (value ? `$ ${value}` : "-"),
      },
      {
        field: "patientResponsibility",
        flex: 0.2,
        minWidth: 40,
        disableColumnMenu: true,
        headerName: "Patient Responsibility",
        renderCell: ({ row }) =>
          row?.patientResponsibility ? `$ ${row?.patientResponsibility}` : "-",
      },
      {
        field: "adjustment",
        flex: 0.2,
        minWidth: 40,
        disableColumnMenu: true,
        headerName: "Adjustment",
        renderCell: ({ row }) =>
          row?.adjustment ? `$ ${row?.adjustment}` : "-",
      },
      {
        field: "invoiceId",
        flex: 0.4,
        minWidth: 70,
        disableColumnMenu: true,
        headerName: "Invoice",
        sortable: false,
        renderCell: ({ row }) => {
          // Linked PR invoice (super bill) generated from this claim's PR balance.
          const linkedInvoiceUuid = row?.invoiceId;
          return linkedInvoiceUuid ? (
            <Text
              isTabletext
              color={"#004186"}
              sx={{ cursor: "pointer", textDecoration: "underline" }}
              onClick={() => setSelectedSuperBillUuid(linkedInvoiceUuid)}
            >
              {getFormattedId(linkedInvoiceUuid)}
            </Text>
          ) : (
            <Text isTabletext>-</Text>
          );
        },
      },
      {
        field: "totalWriteOff",
        flex: 0.2,
        minWidth: 40,
        disableColumnMenu: true,
        headerName: "Write-Off",
        sortable: false,
        renderCell: ({ row }) =>
          row?.totalWriteOff ? `$ ${row?.totalWriteOff}` : "-",
      },
      {
        field: "active",
        flex: 0.4,
        minWidth: 110,
        disableColumnMenu: true,
        headerName: "Status",
        sortable: false,
        renderCell: ({ row }) => {
          return (
            row?.claimStatus && (
              <StatusChip
                label={
                  row?.claimStatus === "CANCELLED"
                    ? "Canceled"
                    : capitalize(replaceUnderScoreAddSpace(row?.claimStatus))
                }
                variant={ClaimStatusVariantMap[row?.claimStatus] ?? "default"}
              />
            )
          );
        },
      },
      {
        field: "postingStatus",
        flex: 0.28,
        minWidth: 90,
        disableColumnMenu: true,
        headerName: "Posting Status",
        sortable: false,
        // Backend-derived ERA posting status (UNPAID / PARTIALLY_PAID / PAID),
        // shown only on the parent claim row. CPT child rows carry their own
        // posting status in the Status column.
        renderCell: ({ row }) => {
          if (row?.__cptChild || !row?.postingStatus) return null;
          return (
            <StatusChip
              label={capitalize(replaceUnderScoreAddSpace(row?.postingStatus))}
              variant={ClaimStatusVariantMap[row?.postingStatus] ?? "default"}
            />
          );
        },
      },
      {
        field: "Action",
        flex: 0.2,
        width: 40,
        hideSortIcons: true,
        renderCell: ({ row }) => {
          if (row?.__cptChild) return null;
          return (
            <ActionMenu
              options={roleFilter({
                list: [
                  { value: "view_claim", label: "View Claim" },
                  {
                    value: "edit_claim",
                    label: "Edit Claim",
                    isDisabled:
                      row?.claimStatus !== "DRAFT" &&
                      row?.claimStatus !== "REJECTED",
                    hoverMsg: "You can only edit a draft & Rejected claim",
                  },
                  {
                    value: "change_status",
                    label: "Update Status",
                    isDisabled: row?.claimType === "electronic_claim",
                    hoverMsg:
                      "You cannot change the status of an electronic claim",
                  },
                  {
                    value: "fax_claim",
                    label: "Fax Claim",
                  },
                  // Hidden until the Add Payment flow is implemented; the
                  // option has no handler yet.
                  // {
                  //   value: "add_payment",
                  //   label: "Add Payment",
                  // },
                  // ECRH-5392: Hide the "Update ERA/EOB Details" option under
                  // Patient Charting → Financial → Claim → Action as per requirement.
                  // {
                  //   value: "update_era_eob_details",
                  //   label: "Update ERA/EOB Details",
                  //   // A fully-paid claim (all CPTs posted) can no longer have
                  //   // its ERA/EOB details edited; keep the action visible but
                  //   // disabled. Unpaid / partially-paid claims stay editable.
                  //   isDisabled: row?.postingStatus === "PAID",
                  //   hoverMsg:
                  //     row?.postingStatus === "PAID"
                  //       ? "This claim is fully paid; ERA/EOB details can no longer be updated"
                  //       : undefined,
                  // },
                  {
                    value: "create_invoice_for_pr",
                    label: "Create Invoice for PR",
                    // A claim can carry only one PR invoice; the listing marks
                    // the ones already generated so a duplicate cannot be
                    // created. A partially posted claim still has CPT lines
                    // waiting on the ERA/EOB, so its patient responsibility is
                    // not final either.
                    isDisabled:
                      !row?.patientResponsibility ||
                      row?.patientResponsibility <= 0 ||
                      row?.superBillGenerated === true ||
                      row?.postingStatus ===
                        CLAIM_POSTING_STATUS_PARTIALLY_PAID,
                    hoverMsg:
                      row?.superBillGenerated === true
                        ? "A PR invoice has already been generated for this claim"
                        : !row?.patientResponsibility ||
                            row?.patientResponsibility <= 0
                          ? "Patient responsibility must be greater than zero to create an invoice"
                          : CLAIM_NOT_COMPLETELY_POSTED_MSG,
                  },
                  {
                    value: "resubmit_claim",
                    label: "Resubmit Claim",
                    isDisabled: !["DENIED", "REJECTED"].includes(
                      row?.claimStatus
                    ),
                    hoverMsg:
                      "You can only resubmit a denied or rejected claim",
                  },
                  {
                    value: "print_on_cms_form",
                    label: "Print Claim Data on CMS 1500 Form",
                  },
                  // Hidden until Show EOB is implemented; the option has no
                  // handler yet.
                  // {
                  //   value: "show_EOB",
                  //   label: "Show EOB (2)",
                  // },
                  {
                    value: "waystar_response",
                    label: "Clearing House Response",
                    isDisabled: row?.error === null,
                    hoverMsg:
                      row?.error === null
                        ? "Claim response is not available"
                        : undefined,
                  },
                  // Hidden until secondary claim generation is implemented;
                  // the option has no handler yet.
                  // {
                  //   value: "generate_secondary_claim",
                  //   label: "Generate Secondary Claim",
                  // },
                  {
                    value: "send_email",
                    label: "Send Email",
                  },
                  // { value: "delete_claim", label: "Delete Claim" },
                ],
                compWith: {
                  view_claim: hasViewClaims,
                  edit_claim: hasUpdateClaims,
                  change_status: hasUpdateClaimStatus,
                  resubmit_claim: hasUpdateClaims,
                  waystar_response: hasUpdateWaystarResponse,
                },
              })}
              onActionClick={(v) => {
                if (v === "edit_claim") {
                  navigate(
                    PROVIDER_PATHS.BILLING +
                      `claims/edit-claim?patientId=${row?.patientId}&claimId=${row?.uuid}&claimStatus=UPDATED`,
                    { state: { from: fromPatientChart } }
                  );
                  // setSearchParams({
                  //   patientId: row?.patientId,
                  //   claimId: row?.uuid,
                  //   claimStatus: "UPDATED",
                  // });
                } else if (v === "delete_claim") {
                  openDeleteModal(row.uuid);
                } else if (v === "view_claim") {
                  // Every claim type opens the read-only claim details view
                  // (Service/Provider/Diagnosis/CPT). The CMS 1500 form stays
                  // reachable through the separate print action.
                  navigate(
                    PROVIDER_PATHS.BILLING +
                      `claims/edit-claim?patientId=${row?.patientId}&claimId=${row?.uuid}&claimStatus=UPDATED&readOnly=true`,
                    { state: { from: fromPatientChart } }
                  );
                  // Paper claims used to open the CMS 1500 drawer here, which
                  // duplicated "Print Claim Data on CMS 1500 Form".
                  // const claimType = row?.claimType?.toLowerCase();
                  // if (claimType === "paper_claim" || claimType === "paper") {
                  //   viewPaperClaimDisc.open(row?.uuid);
                  // }
                  //   setSearchParams({
                  //     patientId: row?.patientId,
                  //     claimId: row?.uuid,
                  //     readOnly: "true",
                  //   });
                } else if (v === "update_era_eob_details") {
                  queryClient.prefetchQuery({
                    queryKey: ["Get-claim-by-id", row?.uuid],
                    queryFn: async () =>
                      new ClaimControllerApi(getConfigObject())
                        .getClaimById(row?.uuid, getXtenantId())
                        .then((it) => it?.data),
                  });
                  navigate(
                    PROVIDER_PATHS.BILLING +
                      `update-era-eob?patientId=${row?.patientId}&claimId=${row?.uuid}`,
                    { state: { from: fromPatientChart } }
                  );
                  // setSearchParams({
                  //   patientId: row?.patientId,
                  //   claimId: row?.uuid,
                  // });
                } else if (v === "create_invoice_for_pr") {
                  navigate(
                    `${PROVIDER_PATHS.BILLING}create-pr-invoice/${row?.patientId}/${row?.uuid}`,
                    { state: { from: fromPatientChart } }
                  );
                } else if (v === "resubmit_claim") {
                  navigate(
                    PROVIDER_PATHS.BILLING +
                      `claims/edit-claim?patientId=${row?.patientId}&claimId=${row?.uuid}&claimStatus=UPDATED`,
                    { state: { from: fromPatientChart } }
                  );
                  // navSelectClaimTypeDisc.open(row.uuid);
                } else if (v === "waystar_response") {
                  waystarResponseDisc.open(row?.error);
                } else if (v === "fax_claim") {
                  createFaxClaimDisc.open({
                    ...DOCUMENT_INITIAL_VALUE,
                    sCPFromName: row.providerName,
                    documentDescription: "Fax Claim",
                  });
                  setSearchParams({
                    patientId: row?.patientId,
                    claimId: row?.uuid,
                  });
                } else if (v === "change_status") {
                  changeClaimStatusDisclosureHost.open(row);
                } else if (v === "send_email") {
                  sendMailDisclosure.open([
                    {
                      uuid: row?.patientId,
                      name: row?.patientName,
                      claimUuid: row?.uuid,
                    },
                  ]);
                } else if (v === "print_on_cms_form") {
                  viewPaperClaimDisc.open(row?.uuid);
                }
              }}
            />
          );
        },
      },
    ],
    ["dateOfService", "patientName", "total"]
  );

  // Final columns (Action filtered by permission). The detail row spans every
  // column so the ERA procedure table renders full width.
  const gridColumns = filterActionCol(COLUMNS, hasReadOnly);
  gridColumns[0] = {
    ...gridColumns[0],
    colSpan: (params) =>
      params?.row?.__detail ? gridColumns.length : undefined,
  };

  // Collapse duplicate claim records into one accordion parent (keeps row ids
  // unique) and inject a full-width detail row under the expanded claim, which
  // renders that claim's ERA procedure breakdown.
  const seenClaimIds = new Set<string>();
  const dedupedRows = filteredRows.filter((claim) => {
    const id = claim?.uuid;
    if (!id) return true;
    if (seenClaimIds.has(id)) return false;
    seenClaimIds.add(id);
    return true;
  });
  const displayRows = dedupedRows.flatMap((claim) =>
    claim?.uuid && claim.uuid === expandedClaimId
      ? [
          claim,
          {
            id: `${claim.uuid}__detail`,
            __detail: true,
            parentUuid: claim.uuid,
          },
        ]
      : [claim]
  );

  function archiveHandler<T>(value: boolean, id: T) {
    return archieveAPI({
      claimId: id?.toString() ?? "",
      status: value,
      xTENANTID: getXtenantId(),
    }).then((res) => {
      refetch();
      return res;
    });
  }

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {selectedSuperBillUuid && (
        <PrintSuperBill
          key={selectedSuperBillUuid}
          superBillUuid={selectedSuperBillUuid}
          patientName={
            claimsList?.data?.content?.find(
              (item) => item?.invoiceId === selectedSuperBillUuid
            )?.patientName
          }
          onClose={() => {
            setSelectedSuperBillUuid(null);
          }}
        />
      )}
      <UploadFaxDocumentDrawer
        disclosureHost={createFaxClaimDisc}
        allowSecondDocument={true}
      />

      {changeClaimStatusDisclosureHost.opened && (
        <ChangeClaimStatus
          open={changeClaimStatusDisclosureHost.opened}
          onClose={changeClaimStatusDisclosureHost.close}
          disclosureHost={changeClaimStatusDisclosureHost}
        />
      )}

      {sendMailDisclosure.opened && (
        <SendBulkEmailModal
          disableAddPatient
          // Same Email Claim flow as the Billing claims list — fixed recipient.
          lockRecipients
          allowAttachment
          disClosureHost={sendMailDisclosure}
          onSave={() => {
            refetch();
          }}
        />
      )}

      <DeleteModal
        w="380px"
        open={openedDeleteModal}
        onClose={closeDeleteModal}
        id={deleteId}
        onConfirmation={(id) => {
          return archiveHandler(true, id);
        }}
        content={"You want to delete this Claim"}
      />

      <ClearingHouseResponseModal
        response={waystarResponseDisc.data}
        open={waystarResponseDisc.opened}
        onClose={waystarResponseDisc.close}
      />

      <NavSelectClaimTypeModal
        disclosureHost={navSelectClaimTypeDisc}
        onNextClick={(v) => {
          if (v === "WAYSTAR") {
            handleElectronicClaim({
              claimType: ClaimRequestVendorTypeEnum.Waystar,
              claimUuid: navSelectClaimTypeDisc.data ?? "",
            });
          } else if (v === "CLAIM_MD") {
            handleElectronicClaim({
              claimType: ClaimRequestVendorTypeEnum.ClaimMd,
              claimUuid: navSelectClaimTypeDisc.data ?? "",
            });
          } else if (v === "STEDI") {
            handleElectronicClaim({
              claimType: ClaimRequestVendorTypeEnum.Stedi,
              claimUuid: navSelectClaimTypeDisc.data ?? "",
            });
          }
        }}
      />

      <ViewPaperClaimDrawer disclosureHost={viewPaperClaimDisc} />

      <GlobalTableHeightWrapper marginTop={0}>
        <CustomDataGridV2
          getRowId={(row) =>
            row?.__detail || row?.__cptChild ? row?.id : row?.uuid
          }
          getRowHeight={(params) => (params?.model?.__detail ? "auto" : null)}
          rows={displayRows}
          // rowHeight={60}
          columns={gridColumns}
          rowCount={claimsList?.data?.page?.totalElements ?? 10}
          disableRowSelectionOnClick
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          onSortModelChange={setGridSorting}
          loading={claimsDataLoading || isClaimFetching}
        />
      </GlobalTableHeightWrapper>
    </Box>
  );
};

export default PatientClaims;

const ClaimStatusVariantMap: Record<
  string,
  "success" | "error" | "warning" | "info" | "default" | "neutral" | "pending"
> = {
  ACCEPTED: "success",
  DRAFT: "warning",
  REJECTED: "error",
  SUBMITTED: "info",
  DENIED: "error",
  PASSED: "success",
  PENDING: "pending",
  UNPAID: "neutral",
  PARTIALLY_PAID: "warning",
  PAID: "success",
};
